/**
 * AuditContext — central audit log store.
 *
 * All auditable categories write into this context. The audit report page reads
 * from it. Booking history from BookingDatabaseContext is surfaced here without
 * duplication by projecting BookingHistoryRecord → AuditLogEntry on read.
 *
 * In **live mode** the context reads from / writes to the backend API.
 * In **demo mode** it uses sessionStorage as before.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDataMode } from '@/contexts/DataModeContext';
import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type AuditAction = 'New' | 'Edit' | 'Delete';

export type AuditCategory =
  | 'Bookings'
  | 'Settings – General'
  | 'Settings – Booking Types'
  | 'Settings – Timezones'
  | 'Settings – Group Setup'
  | 'Settings – Integrations'
  | 'Settings – Audit'
  | 'Roles & Permissions'
  | 'Users';

export interface AuditFieldChange {
  field: string;
  before: string | null;
  after: string | null;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;           // ISO string
  user: string;                // display name
  category: AuditCategory;
  action: AuditAction;
  entity: string;              // e.g. booking ref, resource name, setting key
  changes: AuditFieldChange[];
  /** true for entries projected from BookingDatabaseContext (not stored here) */
  isBookingProjection?: boolean;
}

export interface AuditCategoryConfig {
  category: AuditCategory;
  label: string;
  description: string;
  enabled: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default category configs
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_AUDIT_CATEGORIES: AuditCategoryConfig[] = [
  {
    category: 'Bookings',
    label: 'Bookings',
    description: 'All create, edit and delete actions on bookings.',
    enabled: true,
  },
  {
    category: 'Settings – General',
    label: 'Settings – General',
    description: 'Changes to general application settings.',
    enabled: true,
  },
  {
    category: 'Settings – Booking Types',
    label: 'Settings – Booking Types',
    description: 'Booking type configuration changes.',
    enabled: true,
  },
  {
    category: 'Settings – Timezones',
    label: 'Settings – Timezones',
    description: 'Timezone configuration changes.',
    enabled: true,
  },
  {
    category: 'Settings – Group Setup',
    label: 'Settings – Group Setup',
    description: 'Planner group changes.',
    enabled: true,
  },
  {
    category: 'Settings – Integrations',
    label: 'Settings – Integrations',
    description: 'Integration configuration changes.',
    enabled: true,
  },
  {
    category: 'Settings – Audit',
    label: 'Settings – Audit',
    description: 'Changes to audit settings themselves.',
    enabled: true,
  },
  {
    category: 'Roles & Permissions',
    label: 'Roles & Permissions',
    description: 'Role and permission changes.',
    enabled: true,
  },
  {
    category: 'Users',
    label: 'Users',
    description: 'User account creation, edits and deactivation.',
    enabled: true,
  },
];

const STORAGE_KEY = 'audit_log_entries';
const CATEGORY_STORAGE_KEY = 'audit_category_config_v2';

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

function getApiBaseUrl(): string {
  const configuredUrl = getApiUrl();
  if (configuredUrl) return configuredUrl.replace(/\/$/, '');
  return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000';
}

interface AuditLogApiEntry {
  id: number;
  organisationId: string;
  companyId: string | null;
  timestamp: string;
  userId: string | null;
  userDisplayName: string | null;
  category: string;
  action: string;
  entity: string;
  changesJson: string | null;
  createdAt: string;
}

function apiEntryToAuditLogEntry(e: AuditLogApiEntry): AuditLogEntry {
  let changes: AuditFieldChange[] = [];
  if (e.changesJson) {
    try {
      changes = JSON.parse(e.changesJson);
    } catch {
      // ignore
    }
  }
  return {
    id: String(e.id),
    timestamp: e.timestamp,
    user: e.userDisplayName || e.userId || 'Unknown',
    category: e.category as AuditCategory,
    action: e.action as AuditAction,
    entity: e.entity,
    changes,
  };
}

/**
 * Error thrown by {@link fetchAuditLogEntries} when the API responds with a
 * non-OK status. Carries the HTTP status so callers can distinguish an
 * authorization failure (403 — the caller lacks the audit-log-report
 * permission) from other errors and surface a friendly message.
 */
export class AuditLogFetchError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuditLogFetchError';
    this.status = status;
  }
}

export interface FetchAuditLogParams {
  category?: string;
  action?: string;
  user?: string;
  from?: string;
  to?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDir?: string;
}

export async function fetchAuditLogEntries(
  params: FetchAuditLogParams = {},
): Promise<{ entries: AuditLogEntry[]; total: number }> {
  const baseUrl = getApiBaseUrl();
  const searchParams = new URLSearchParams();
  if (params.category) searchParams.set('category', params.category);
  if (params.action) searchParams.set('action', params.action);
  if (params.user) searchParams.set('user', params.user);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.keyword) searchParams.set('keyword', params.keyword);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.sortField) searchParams.set('sortField', params.sortField);
  if (params.sortDir) searchParams.set('sortDir', params.sortDir);

  const url = `${baseUrl}/api/AuditLog?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok)
    throw new AuditLogFetchError(response.status, `Failed to fetch audit log: ${response.statusText}`);
  const data = await response.json();
  return {
    entries: (data.entries as AuditLogApiEntry[]).map(apiEntryToAuditLogEntry),
    total: data.total as number,
  };
}

async function fetchCategoryConfigs(): Promise<AuditCategoryConfig[]> {
  const baseUrl = getApiBaseUrl();
  const response = await authFetch(`${baseUrl}/api/AuditCategoryConfig`);
  if (!response.ok) throw new Error(`Failed to fetch category configs: ${response.statusText}`);
  const data = await response.json();
  return (data as Array<{ category: string; label: string; description: string | null; enabled: boolean }>).map(
    (c) => ({
      category: c.category as AuditCategory,
      label: c.label,
      description: c.description ?? '',
      enabled: c.enabled,
    }),
  );
}

export async function postAuditLogEntry(entry: {
  category: string;
  action: string;
  entity: string;
  user: string;
  changesJson: string;
}): Promise<AuditLogApiEntry> {
  const baseUrl = getApiBaseUrl();
  const response = await authFetch(`${baseUrl}/api/AuditLog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: entry.user,
      userDisplayName: entry.user,
      category: entry.category,
      action: entry.action,
      entity: entry.entity,
      changesJson: entry.changesJson,
    }),
  });
  if (!response.ok) throw new Error(`Failed to create audit log entry: ${response.statusText}`);
  return response.json();
}

async function putCategoryConfig(
  category: string,
  enabled: boolean,
  label?: string,
  description?: string,
): Promise<void> {
  const baseUrl = getApiBaseUrl();
  await authFetch(`${baseUrl}/api/AuditCategoryConfig/${encodeURIComponent(category)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled, label: label ?? category, description }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────────────────────────────────────

interface AuditContextType {
  entries: AuditLogEntry[];
  /** Log a new audit entry. Returns silently if category is disabled. */
  log: (entry: Omit<AuditLogEntry, 'id' | 'timestamp'>) => void;
  categoryConfigs: AuditCategoryConfig[];
  updateCategoryConfig: (category: AuditCategory, enabled: boolean) => void;
  isCategoryEnabled: (category: AuditCategory) => boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context & Provider
// ─────────────────────────────────────────────────────────────────────────────

const AuditContext = createContext<AuditContextType | null>(null);

export function AuditProvider({ children }: { children: React.ReactNode }) {
  const { getScreenMode } = useDataMode();
  const isLive = getScreenMode('settings') === 'live';

  const [entries, setEntries] = useState<AuditLogEntry[]>(() => {
    if (isLive) return [];
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as AuditLogEntry[];
    } catch {
      // ignore
    }
    return buildDemoEntries();
  });

  const [categoryConfigs, setCategoryConfigs] = useState<AuditCategoryConfig[]>(() => {
    if (isLive) return DEFAULT_AUDIT_CATEGORIES;
    try {
      const raw = sessionStorage.getItem(CATEGORY_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as AuditCategoryConfig[];
        const merged = DEFAULT_AUDIT_CATEGORIES.map((def) => {
          const found = saved.find((s) => s.category === def.category);
          return found ?? def;
        });
        return merged;
      }
    } catch {
      // ignore
    }
    return DEFAULT_AUDIT_CATEGORIES;
  });
  const liveLoadInFlightRef = useRef<Promise<void> | null>(null);

  // Fetch live data on mount (or when switching to live mode)
  const prevModeRef = useRef(isLive);
  useEffect(() => {
    if (!isLive) {
      // Switching from live → demo: restore demo entries
      if (prevModeRef.current) {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY);
          setEntries(raw ? JSON.parse(raw) : buildDemoEntries());
        } catch {
          setEntries(buildDemoEntries());
        }
        try {
          const raw = sessionStorage.getItem(CATEGORY_STORAGE_KEY);
          if (raw) {
            const saved = JSON.parse(raw) as AuditCategoryConfig[];
            setCategoryConfigs(DEFAULT_AUDIT_CATEGORIES.map((def) => {
              const found = saved.find((s: AuditCategoryConfig) => s.category === def.category);
              return found ?? def;
            }));
          } else {
            setCategoryConfigs(DEFAULT_AUDIT_CATEGORIES);
          }
        } catch {
          setCategoryConfigs(DEFAULT_AUDIT_CATEGORIES);
        }
      }
      prevModeRef.current = false;
      return;
    }
    prevModeRef.current = true;

    if (liveLoadInFlightRef.current) return;

    const request = Promise.all([
      fetchAuditLogEntries({ page: 1, pageSize: 100, sortField: 'timestamp', sortDir: 'desc' })
        .then((result) => setEntries(result.entries))
        .catch((err) => console.error('Failed to fetch audit entries:', err)),
      fetchCategoryConfigs()
        .then((configs) => {
          if (configs.length > 0) {
            const merged = DEFAULT_AUDIT_CATEGORIES.map((def) => {
              const found = configs.find((c) => c.category === def.category);
              return found ?? def;
            });
            setCategoryConfigs(merged);
          }
        })
        .catch((err) => console.error('Failed to fetch category configs:', err)),
    ]).then(() => undefined);

    liveLoadInFlightRef.current = request;
    void request.finally(() => {
      if (liveLoadInFlightRef.current === request) {
        liveLoadInFlightRef.current = null;
      }
    });
  }, [isLive]);

  // Persist entries to session storage whenever they change (demo mode only)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isLive) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
      } catch {
        // quota exceeded — ignore
      }
    }, 500);
  }, [entries, isLive]);

  // Persist category config (demo mode only)
  useEffect(() => {
    if (isLive) return;
    try {
      sessionStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(categoryConfigs));
    } catch {
      // ignore
    }
  }, [categoryConfigs, isLive]);

  const isCategoryEnabled = useCallback(
    (category: AuditCategory): boolean => {
      const cfg = categoryConfigs.find((c) => c.category === category);
      return cfg ? cfg.enabled : true;
    },
    [categoryConfigs],
  );

  const log = useCallback(
    (entry: Omit<AuditLogEntry, 'id' | 'timestamp'>) => {
      if (!isCategoryEnabled(entry.category)) return;

      if (isLive) {
        const changesJson = JSON.stringify(entry.changes);
        postAuditLogEntry({
          category: entry.category,
          action: entry.action,
          entity: entry.entity,
          user: entry.user,
          changesJson,
        })
          .then((apiEntry) => {
            const mapped = apiEntryToAuditLogEntry(apiEntry);
            setEntries((prev) => [mapped, ...prev]);
          })
          .catch((err) => console.error('Failed to log audit entry:', err));
        return;
      }

      const newEntry: AuditLogEntry = {
        ...entry,
        id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        timestamp: new Date().toISOString(),
      };
      setEntries((prev) => [newEntry, ...prev]);
    },
    [isCategoryEnabled, isLive],
  );

  const updateCategoryConfig = useCallback(
    (category: AuditCategory, enabled: boolean) => {
      setCategoryConfigs((prev) =>
        prev.map((c) => (c.category === category ? { ...c, enabled } : c)),
      );

      if (isLive) {
        const cfg = categoryConfigs.find((c) => c.category === category);
        putCategoryConfig(category, enabled, cfg?.label, cfg?.description).catch((err) =>
          console.error('Failed to update category config:', err),
        );
        return;
      }

      // Log the change itself (always, even if audit settings category is disabled)
      const auditEntry: AuditLogEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        timestamp: new Date().toISOString(),
        user: 'Admin',
        category: 'Settings – Audit',
        action: 'Edit',
        entity: category,
        changes: [
          {
            field: 'Auditing enabled',
            before: String(!enabled),
            after: String(enabled),
          },
        ],
      };
      setEntries((prev) => [auditEntry, ...prev]);
    },
    [isLive, categoryConfigs],
  );

  const value = useMemo(
    () => ({ entries, log, categoryConfigs, updateCategoryConfig, isCategoryEnabled }),
    [entries, log, categoryConfigs, updateCategoryConfig, isCategoryEnabled],
  );

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
}

export function useAudit() {
  const ctx = useContext(AuditContext);
  if (!ctx) throw new Error('useAudit must be used inside AuditProvider');
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────────
// Demo seed data
// ─────────────────────────────────────────────────────────────────────────────

function ts(daysAgo: number, hour = 9, min = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
}

function buildDemoEntries(): AuditLogEntry[] {
  return [
    {
      id: 'demo-1',
      timestamp: ts(0, 10, 15),
      user: 'Sarah Johnson',
      category: 'Bookings',
      action: 'New',
      entity: 'BK-2025-001',
      changes: [
        { field: 'Project', before: null, after: 'Alpha Platform' },
        { field: 'Resource', before: null, after: 'Tom Harris' },
        { field: 'Start Date', before: null, after: '2025-08-01' },
        { field: 'End Date', before: null, after: '2025-08-15' },
        { field: 'Status', before: null, after: 'Planned' },
      ],
    },
    {
      id: 'demo-2',
      timestamp: ts(0, 11, 42),
      user: 'Marcus Lee',
      category: 'Bookings',
      action: 'Edit',
      entity: 'BK-2025-002',
      changes: [
        { field: 'End Date', before: '2025-07-20', after: '2025-07-28' },
        { field: 'Status', before: 'Planned', after: 'Confirmed' },
      ],
    },
    {
      id: 'demo-4',
      timestamp: ts(1, 14, 30),
      user: 'Admin',
      category: 'Settings – General',
      action: 'Edit',
      entity: 'Business Hours',
      changes: [
        { field: 'Business Hours Start', before: '8', after: '9' },
        { field: 'Business Hours End', before: '17', after: '18' },
      ],
    },
    {
      id: 'demo-6',
      timestamp: ts(2, 16, 22),
      user: 'Sarah Johnson',
      category: 'Bookings',
      action: 'Delete',
      entity: 'BK-2025-003',
      changes: [
        { field: 'Project', before: 'Legacy Migration', after: null },
        { field: 'Resource', before: 'James Wu', after: null },
        { field: 'Start Date', before: '2025-07-10', after: null },
      ],
    },
    {
      id: 'demo-7',
      timestamp: ts(3, 11, 0),
      user: 'Admin',
      category: 'Settings – Booking Types',
      action: 'Edit',
      entity: 'Project Booking',
      changes: [
        { field: 'Auto Update Timesheet', before: 'false', after: 'true' },
      ],
    },
    {
      id: 'demo-8',
      timestamp: ts(3, 15, 45),
      user: 'Anna Reid',
      category: 'Roles & Permissions',
      action: 'Edit',
      entity: 'Planner Role',
      changes: [
        { field: 'Can Delete Bookings', before: 'false', after: 'true' },
        { field: 'Can View Reports', before: 'false', after: 'true' },
      ],
    },
    {
      id: 'demo-11',
      timestamp: ts(5, 14, 10),
      user: 'Admin',
      category: 'Settings – Timezones',
      action: 'Edit',
      entity: 'System Default Timezone',
      changes: [
        { field: 'Default Timezone', before: 'UTC', after: 'Europe/London' },
      ],
    },
    {
      id: 'demo-12',
      timestamp: ts(6, 8, 45),
      user: 'Anna Reid',
      category: 'Users',
      action: 'New',
      entity: 'james.wu@example.com',
      changes: [
        { field: 'Email', before: null, after: 'james.wu@example.com' },
        { field: 'Role', before: null, after: 'Planner' },
        { field: 'Status', before: null, after: 'Active' },
      ],
    },
    {
      id: 'demo-13',
      timestamp: ts(7, 13, 15),
      user: 'Sarah Johnson',
      category: 'Bookings',
      action: 'Edit',
      entity: 'BK-2025-004',
      changes: [
        { field: 'Start Date', before: '2025-08-05', after: '2025-08-12' },
        { field: 'End Date', before: '2025-08-10', after: '2025-08-19' },
      ],
    },
    {
      id: 'demo-15',
      timestamp: ts(10, 11, 30),
      user: 'Admin',
      category: 'Settings – Integrations',
      action: 'Edit',
      entity: 'Calendar Sync',
      changes: [
        { field: 'Enabled', before: 'false', after: 'true' },
      ],
    },
  ];
}
